import { FC } from 'react'
import { ApmRoute } from '@elastic/apm-rum-react'
import { RouteProps, useLocation } from 'react-router-dom'

import config from '../config/app-config'
import AnimatedRedirect from '../features/animation/AnimatedRedirect'
import { useAuth } from '../features/auth/AuthContext'

export type PrivateRouteProps = RouteProps

/**
 * A Route which requires the user to be authenticated before mounting the Route's children.
 *
 * If the user is not authenticated, redirects back to `/login` with info about what the user
 * was trying to access.
 */
const PrivateRoute: FC<PrivateRouteProps> = (props) => {
  const { pathname, search } = useLocation()
  const { isAuthenticated, isLoading, user } = useAuth()
  const newSearch: string = search.includes('redirectTo')
    ? ''
    : `?redirectTo=${pathname}`

  if (isLoading) return null

  // Handle provider access checks for authenticated users with staff
  if (user?.staff) {
    // Check if user has full access (active sites AND activated staff)
    const hasActiveSiteStaff = user.staff.hasActiveSiteStaff ?? false
    const isStaffActivated = user.staff.activated ?? false
    const userHasAccess = hasActiveSiteStaff && isStaffActivated

    // If user doesn't have full access and not already in provider flow, redirect
    if (!userHasAccess && !pathname.startsWith('/provider')) {
      return <AnimatedRedirect to="/provider" />
    }
  }

  if (isAuthenticated && search.includes('redirectTo')) {
    const params = new URLSearchParams(search)
    return <AnimatedRedirect to={params.get('redirectTo') || ''} />
  }

  return !isAuthenticated ? (
    <AnimatedRedirect
      to={{
        pathname: config.unAuthenticatedRootUrl,
        state: { from: pathname, isAuthRedirect: true },
        search: search + newSearch,
      }}
    />
  ) : (
    <ApmRoute {...props} />
  )
}

export default PrivateRoute
